import { Card, Table, Tag } from 'antd';
import apiCall from 'apps/investment/apiCalls/order';
import { companyColors } from 'apps/investment/constants';
import {
  fundOrderSideColors,
  fundOrderSideLabels,
} from 'apps/investment/constants/fund-orders';
import {
  fundOrderStatusColors,
  fundOrderStatusLabels,
  orderProgramTypeColors,
  orderProgramTypeLabels,
  orderStatusColors,
  orderStatusCompleted,
  orderTypeColors as fundOrderTypeColors,
  orderTypeLabels as fundOrderTypeLabels,
} from 'apps/investment/constants/fundOrder';
import CashSVG from 'common/svg/cash-svg';
import BooleanIcons from 'common/ui/boolean-icons';
import { Button } from 'common/ui/button';
import InfoCard from 'common/ui/info-card';
import { LabelTooltip } from 'common/ui/label-tooltip';
import { commonDictionary, fundDictionary } from 'config/dictionary';
import { fundExplanation } from 'config/explanation';
import { useFetchApiList } from 'hooks/api';
import { ClockArrowUp, Info, Receipt } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cn } from 'utils/cn';
import { formatCurrency, formatNumber } from 'utils/formatAmount';
import { formatDate, formatDateTime } from 'utils/formatDate';
import { FundOrderAction } from './PostActions';

const ItemDetail = ({ item }) => {
  const {
    id,
    fund_order_id,
    fund_manager,
    fund_code,
    order_side,
    status,
    status_label,
    user_status_label,
    trading_account_code,
    trading_account_manager,
    amount,
    estimate_amount,
    net_amount,
    estimate_net_amount,
    quantity,
    estimate_quantity,
    matched_price,
    fee_amount,
    estimate_fee_amount,
    tax_amount,
    estimate_tax_amount,
    can_cancel,
    fund_customer_id,
    fund_order_status,
    fund_payment_status,
    fund_purchase_date,
    fund_order_closing_time,
    fund_order_time,
    fund_payment_message,
    fund_payment_bank_napas_code,
    fund_payment_bank_account_number,
    fund_payment_bank_account_name,
    fund_payment_bank_name,
    fund_payment_bank_branch_name,
    fund_amount,
    fund_net_amount,
    fund_quantity,
    fund_matched_price,
    fund_fee_amount,
    fund_tax_amount,
    fund_can_cancel,
    fund_updated_at,
    user,
    created_at,
    updated_at,
    product_id,
    order_date,
    program_type,
    order_source,
  } = item;

  const isUpdateable =
    trading_account_manager === 'nhsv' && status !== orderStatusCompleted;

  const {
    data: statusHistories = [],
    load: loadStatusHistories,
    isLoading: isLoadingHistories,
  } = useFetchApiList(apiCall.statusHistories, { resourceName: 'items' });

  useEffect(() => {
    loadStatusHistories({ id });
  }, [id]);

  const orderStatusSystemLabels = {
    '-1': 'draft',
    '0': 'new',
    '1': 'pending',
    '2': 'waiting_for_approve',
    '3': 'fund_approve',
    '4': 'settled',
    '5': 'completed',
    '6': 'cancelled',
  };

  const statusHistoryColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => (
        <Tag color={orderStatusColors[s]}>
          {orderStatusSystemLabels[String(s)] ?? s}
        </Tag>
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      render: (note) => note || '-',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v) => formatDateTime(v),
    },
  ];

  return (
    <div>
      <Card
        className={cn('my-4 shadow-md overflow-hidden border-2', {
          'border-green-500': order_side === 'buy',
          'border-blue-500': order_side === 'sell',
        })}
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <div className="space-x-2 flex items-center">
              {isUpdateable && (
                <Button asChild>
                  <Link to={`/investment/fund-orders/${id}/update`}>
                    Cập nhật dữ liệu quỹ
                  </Link>
                </Button>
              )}
              <FundOrderAction record={item} />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard
              label={'Lệnh'}
              value={
                <Tag color={fundOrderSideColors[order_side]}>
                  {fundOrderSideLabels[order_side] || order_side}
                </Tag>
              }
            />
            <InfoCard
              label={'Trạng thái'}
              value={
                <Tag color={orderStatusColors[status]}>
                  {status_label ? `${status_label} (${status})` : status}
                </Tag>
              }
            />

            <InfoCard
              label={'Trạng thái trên app'}
              value={
                user_status_label ? (
                  <Tag color={orderStatusColors[status]}>
                    {user_status_label}
                  </Tag>
                ) : null
              }
            />
            <InfoCard
              label={'Nguồn'}
              value={
                order_source ? (
                  <Tag color={companyColors[order_source]}>{order_source}</Tag>
                ) : null
              }
            />
            <InfoCard
              label="Trading Account Manager"
              value={
                <Tag color={companyColors[trading_account_manager]}>
                  {trading_account_manager}
                </Tag>
              }
            />

            <InfoCard
              label={fundDictionary.fund_code}
              value={
                <Link to={`/admin/products/funds/${product_id}`}>
                  {fund_code} ({fund_manager})
                </Link>
              }
            />
            <InfoCard
              label="Trading Account Code"
              value={trading_account_code}
            />
            <InfoCard
              label="Program Type"
              value={
                <Tag color={orderProgramTypeColors[program_type]}>
                  {orderProgramTypeLabels[program_type]}
                </Tag>
              }
            />
            <InfoCard
              label={'Có thể huỷ'}
              value={<BooleanIcons value={can_cancel} />}
            />
            <InfoCard
              className="break-words"
              label={'User'}
              value={
                user.id && (
                  <Link to={`/investment/users/${user.id}`}>
                    {user?.email || user.id}
                  </Link>
                )
              }
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InfoCard
              label={commonDictionary.created_at}
              value={formatDateTime(created_at)}
            />
            <InfoCard
              label={commonDictionary.updated_at}
              value={formatDateTime(updated_at)}
            />
            <InfoCard label={'Order Date'} value={formatDateTime(order_date)} />
          </div>

          <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
            <CashSVG className="mr-1" />
            Thông tin lệnh
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <InfoCard
              label="Số lượng ước tính"
              value={
                isNaN(estimate_quantity) ? '-' : formatNumber(estimate_quantity)
              }
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={'Số tiền ước tính'}
                  tooltipContent="estimate_amount"
                />
              }
              value={formatCurrency(estimate_amount)}
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={'Số tiền ròng ước tính'}
                  tooltipContent="estimate_net_amount"
                />
              }
              value={formatCurrency(estimate_net_amount)}
            />
            <InfoCard
              label="Phí ước tính"
              value={formatCurrency(estimate_fee_amount)}
            />
            <InfoCard
              label="Thuế ước tính"
              value={formatCurrency(estimate_tax_amount)}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <InfoCard
              label={commonDictionary.quantity}
              value={isNaN(quantity) ? '-' : formatNumber(quantity)}
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={'Số tiền'}
                  tooltipContent={
                    <div>
                      <p>buy amount = số tiền thanh toán mua</p>
                      <p>
                        sell amount = số tiền nhận được (sau khi đã trừ phí)
                      </p>
                    </div>
                  }
                />
              }
              value={formatCurrency(amount)}
            />

            <InfoCard
              label={
                <LabelTooltip
                  label={'Số tiền ròng'}
                  tooltipContent={
                    <div>
                      <p>buy net amount = số lượng mua được * giá khớp</p>
                      <p>sell net amount = số lượng bán * giá khớp</p>
                    </div>
                  }
                />
              }
              value={formatCurrency(net_amount)}
            />
            <InfoCard label="Phí" value={formatCurrency(fee_amount)} />

            <InfoCard label="Thuế" value={formatCurrency(tax_amount)} />
            <InfoCard label="Giá khớp" value={formatNumber(matched_price)} />
          </div>

          <h3 className="text-base font-medium text-gray-800 border-b pt-2 flex items-center">
            <Info className="text-primary size-5 mr-1" />
            Thông tin từ quỹ
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <InfoCard label="Fund Order Status" value={fund_order_status} />
            <InfoCard label="Fund Payment Status" value={fund_payment_status} />
            <InfoCard label="Fund Order ID" value={fund_order_id} />
            <InfoCard label="Fund Customer ID" value={fund_customer_id} />
            <InfoCard
              label="Fund Can Cancel"
              value={<BooleanIcons value={fund_can_cancel} />}
            />

            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_quantity}
                  tooltipContent="fund_quantity"
                />
              }
              value={isNaN(fund_quantity) ? '-' : formatNumber(fund_quantity)}
            />

            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_amount}
                  tooltipContent="fund_amount"
                />
              }
              value={formatCurrency(fund_amount)}
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_net_amount}
                  tooltipContent="fund_net_amount"
                />
              }
              value={formatCurrency(fund_net_amount)}
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_fee_amount}
                  tooltipContent="fund_fee_amount"
                />
              }
              value={formatCurrency(fund_fee_amount)}
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_tax_amount}
                  tooltipContent="fund_tax_amount"
                />
              }
              value={formatCurrency(fund_tax_amount)}
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_matched_price}
                  tooltipContent="fund_matched_price"
                />
              }
              value={formatNumber(fund_matched_price)}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4"></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_order_time}
                  tooltipContent={
                    <div>
                      <div>{fundExplanation.fund_order_time}</div>
                      <div>{'fund_order_time'}</div>
                    </div>
                  }
                />
              }
              value={formatDateTime(fund_order_time)}
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_purchase_date}
                  tooltipContent={
                    <div>
                      <div>{fundExplanation.fund_purchase_date}</div>
                      <div>{'fund_purchase_date'}</div>
                    </div>
                  }
                />
              }
              value={formatDateTime(fund_purchase_date)}
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_order_closing_time}
                  tooltipContent={
                    <div>
                      <div>{fundExplanation.fund_order_closing_time}</div>
                      <div>{'fund_order_closing_time'}</div>
                    </div>
                  }
                />
              }
              value={formatDateTime(fund_order_closing_time)}
            />
            <InfoCard
              label={
                <LabelTooltip
                  label={fundDictionary.fund_updated_at}
                  tooltipContent={
                    <div>
                      <div>{fundExplanation.fund_updated_at}</div>
                      <div>{'fund_updated_at'}</div>
                    </div>
                  }
                />
              }
              value={formatDateTime(fund_updated_at)}
            />
          </div>

          <h3 className="text-base font-medium text-gray-800 border-b pt-2 flex items-center">
            <Info className="text-primary size-5 mr-1" />
            Thông tin thanh toán
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <InfoCard
              label="Payment Bank Account Name"
              value={fund_payment_bank_account_name}
            />
            <InfoCard
              label="Payment Bank Account Number"
              value={fund_payment_bank_account_number}
            />
            <InfoCard
              label="Payment Bank Name"
              value={fund_payment_bank_name}
            />
            <InfoCard
              label="Payment Bank Napas Code"
              value={fund_payment_bank_napas_code}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoCard
              label="Payment Bank Branch Name"
              value={fund_payment_bank_branch_name}
            />
            <InfoCard label="Payment Message" value={fund_payment_message} />
          </div>
        </div>
      </Card>

      {/* Transaction Section */}
      {item.transaction && (
        <Card className="my-4 shadow-md overflow-hidden border-2 border-blue-400">
          <h3 className="text-base font-medium text-blue-600 border-b border-blue-200 pb-2 mb-4 flex items-center">
            <Receipt className="size-5 mr-1" />
            Thông tin giao dịch (Transaction)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard label="Transaction ID" value={item.transaction.id} />
            <InfoCard label="Trading Account" value={item.transaction.trading_account_code} />
            <InfoCard
              label="Trading Account Manager"
              value={
                <Tag color={companyColors[item.transaction.trading_account_manager]}>
                  {item.transaction.trading_account_manager}
                </Tag>
              }
            />
            <InfoCard label="Date" value={formatDateTime(item.transaction.date)} />
            <InfoCard
              label="Source"
              value={
                <Tag color={companyColors[item.transaction.source]}>
                  {item.transaction.source}
                </Tag>
              }
            />
            <InfoCard
              label="Type"
              value={
                <Tag color={fundOrderTypeColors[item.transaction.type]}>
                  {fundOrderTypeLabels[item.transaction.type] || item.transaction.type}
                </Tag>
              }
            />
            <InfoCard
              label="Status"
              value={
                <Tag color={fundOrderStatusColors[item.transaction.status]}>
                  {fundOrderStatusLabels[item.transaction.status] || item.transaction.status}
                </Tag>
              }
            />
            <InfoCard label="Product Type" value={item.transaction.product_type} />
            <InfoCard label="Product Code" value={item.transaction.product_code} />
            <InfoCard label="Product Alias" value={item.transaction.product_alias} />
            <InfoCard label="Price" value={formatCurrency(item.transaction.price)} />
            <InfoCard label="Quantity" value={formatNumber(item.transaction.quantity)} />
            <InfoCard label="Amount" value={formatCurrency(item.transaction.amount)} />
            <InfoCard label="Net Amount" value={formatCurrency(item.transaction.net_amount)} />
            <InfoCard label="Order Date" value={formatDate(item.transaction.order_date)} />
          </div>
        </Card>
      )}
      {/* Status History Section */}
      <Card className="my-4 shadow-md overflow-hidden border-2 border-amber-400">
        <h3 className="text-base font-medium text-amber-600 border-b border-amber-200 pb-2 mb-4 flex items-center">
          <ClockArrowUp className="size-5 mr-1" />
          Lịch sử trạng thái (Status History)
        </h3>
        <Table
          dataSource={statusHistories}
          columns={statusHistoryColumns}
          rowKey="id"
          loading={isLoadingHistories}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default ItemDetail;
